import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IoMenu, IoClose, IoLogoWhatsapp } from 'react-icons/io5';
import smaLogo from '../../assets/images/logo_lembaga/sma-logo.png';

import './Header.css';

const navLinks = [
  { href: '/about', label: 'Tentang' },
  { href: '/student-life', label: 'Kehidupan Santri' },
  { href: '/blog', label: 'Berita' },
  { href: '/admission', label: 'PSB' },
  { href: '/icharmony', label: 'IC Harmony', isHighlight: true },
];
// Custom hook to track scroll position and header state
// (e.g., scrolled or not)
const useScrollPosition = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const position = window.scrollY;
      setScrollPosition(position);
      setIsScrolled(position > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return { scrollPosition, isScrolled };
};

export function Header() {
  const { isScrolled } = useScrollPosition();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Animation on mount
    const timer = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleNavClick = (e, href) => {
    // If it's an anchor link on the same page
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsOpen(false);
    }
    // If it's a hash link on homepage (e.g., /#programs)
    else if (href.startsWith('/#')) {
      if (location.pathname !== '/') {
        // Let the default navigation happen
        setIsOpen(false);
        return;
      }
      e.preventDefault();
      const element = document.querySelector(href.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsOpen(false);
    }
    // For regular page navigation, let the default happen
    else {
      setIsOpen(false);
    }
  };

  return (
    <>
      <header
        className={`header ${isScrolled ? 'header-scrolled' : ''} ${
          isVisible ? 'header-visible' : ''
        }`}
      >
        <div className="header-container">
          <nav className="header-nav">
            {/* Logo with Premium Flip Animation */}
            <Link to="/" className="header-logo">
              <div className="header-logo-flip-container">
                <div className="header-logo-flipper">
                  {/* Front Side - YBUS Text */}
                  <div className="header-logo-side header-logo-front">
                    <span className="header-logo-title">YBUS</span>
                    <span className="header-logo-subtitle">Yayasan Bahrul Ulum</span>
                  </div>
                  
                  {/* Back Side - Logo Image */}
                  <div className="header-logo-side header-logo-back">
                    <img 
                      src={smaLogo} 
                      alt="YBUS Logo" 
                      className="header-logo-image"
                    />
                  </div>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="header-links">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`header-link ${link.isHighlight ? 'header-link-highlight' : ''}`}
                >
                  {link.label}
                  {!link.isHighlight && <span className="header-link-underline" />}
                </a>
              ))}
            </div>

            {/* Right Side */}
            <div className="header-right">
              <a
                href="https://wa.me/6283157938748?text=Assalamualaikum%20Wr.%20Wb.%20Saya%20ingin%20bertanya%20tentang%20Pondok%20Pesantren%20Bahrul%20Ulum"
                className="header-cta"
                target="_blank"
                rel="noopener noreferrer"
              >
                <IoLogoWhatsapp className="header-cta-icon" />
                Hubungi Kami
              </a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="mobile-menu-btn"
                aria-label="Toggle menu"
              >
                {isOpen ? (
                  <IoClose className="menu-icon" />
                ) : (
                  <IoMenu className="menu-icon" />
                )}
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="mobile-backdrop mobile-backdrop-visible"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu Panel */}
          <div className={`mobile-panel ${isOpen ? 'mobile-panel-open' : ''}`}>
            <div className="mobile-panel-inner">
              {/* Header */}
              <div className="mobile-panel-header">
                <span className="mobile-panel-title">Menu</span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="mobile-close-btn"
                >
                  <IoClose className="menu-icon" />
                </button>
              </div>

              {/* Navigation Links */}
              <nav className="mobile-nav">
                <div className="mobile-nav-links">
                  {navLinks.map((link, index) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className={`mobile-nav-link ${link.isHighlight ? 'mobile-nav-link-highlight' : ''}`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </nav>

              {/* Footer */}
              <div className="mobile-panel-footer">
                <a
                  href="https://wa.me/6283157938748?text=Assalamualaikum%20Wr.%20Wb.%20Saya%20ingin%20bertanya%20tentang%20Pondok%20Pesantren%20Bahrul%20Ulum"
                  className="mobile-cta"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Hubungi Kami
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Header;
